import React, { useRef, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

const FileTypes = {
  JPG: 'jpg',
  PNG: 'png',
  GIF: 'gif',
  WEBP: 'webp',
} as const;

type FileType = (typeof FileTypes)[keyof typeof FileTypes];

interface ImageSequenceScrollerProps {
  totalFrames: number;
  imageFolder: string;
  className?: string;
  fileType: FileType;
}

export const ImageSequenceScroller = ({
  totalFrames,
  imageFolder,
  className,
  fileType = 'jpg',
}: ImageSequenceScrollerProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const createAndTransformImage = useCallback(
    (frameIndex: number): HTMLImageElement => {
      const img = new Image();
      img.src = `${imageFolder}/mockrocket-export_${frameIndex.toString().padStart(4, '0')}.${fileType}`;
      return img;
    },
    [imageFolder, fileType],
  );

  const setupAnimation = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    imageSequence: HTMLImageElement[],
  ) => {
    const end = canvasRef.current?.offsetHeight;
    gsap.to(canvasRef.current, {
      ease: 'none',
      scrollTrigger: {
        trigger: canvasRef.current,
        start: 'bottom bottom',
        end: `+=${end} bottom`,
        scrub: true,
        invalidateOnRefresh: true,
        markers: false,
        onUpdate: (self: { progress: number }) => {
          if (self) {
            const { progress } = self;
            const frameIndex = Math.floor(progress * (imageSequence.length - 1));

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const img = imageSequence[frameIndex];

            const aspectRatio = img.width / img.height;

            let width = canvas.width;
            let height = canvas.height;

            if (canvas.width / canvas.height > aspectRatio) {
              // Canvas is wider, adjust height
              width = canvas.height * aspectRatio;
            } else {
              // Canvas is taller, adjust width
              height = canvas.width / aspectRatio;
            }

            // calculate the position in the canvas for centering the image
            const y = 0;
            const x = (canvas.width - width) / 2;
            ctx.drawImage(img, x, y, width, height);
          }
        },
      },
    });
  };

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const canvas = canvasRef.current!;
      const ctx = canvas.getContext('2d')!;
      const imageSequence = Array.from({ length: totalFrames }, (_, i) => createAndTransformImage(i));

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      setupAnimation(canvas, ctx, imageSequence);
    },
    { scope: canvasRef, dependencies: [imageFolder, totalFrames, fileType, createAndTransformImage] },
  );

  return <canvas ref={canvasRef} className={className}></canvas>;
};
